import { Link } from 'react-router-dom';
import { Lightbulb, AlertTriangle, Info, CheckCircle2, Quote as QuoteIcon } from 'lucide-react';

// Parse inline markdown links: [text](/path) or [text](https://...)
const RichText = ({ text }) => {
  const parts = [];
  const rx = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = rx.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const label = m[1];
    const href = m[2];
    if (href.startsWith('/')) {
      parts.push(
        <Link key={key++} to={href} className="text-orange-400 font-medium underline underline-offset-2 hover:text-orange-300">
          {label}
        </Link>
      );
    } else {
      parts.push(
        <a key={key++} href={href} target="_blank" rel="noopener noreferrer" className="text-orange-400 font-medium underline underline-offset-2 hover:text-orange-300">
          {label}
        </a>
      );
    }
    last = rx.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
};

const CALLOUTS = {
  protip: { icon: Lightbulb, wrap: 'border-orange-500/30 bg-orange-500/[0.07]', ic: 'text-orange-400', title: 'text-orange-300' },
  warning: { icon: AlertTriangle, wrap: 'border-red-500/30 bg-red-500/[0.07]', ic: 'text-red-400', title: 'text-red-300' },
  info: { icon: Info, wrap: 'border-amber-500/30 bg-amber-500/[0.07]', ic: 'text-amber-400', title: 'text-amber-300' },
};

export const ContentBlock = ({ block }) => {
  switch (block.type) {
    case 'h2':
      return (
        <h2
          id={block.id}
          className="scroll-mt-28 text-2xl md:text-3xl font-bold text-white mt-14 mb-5"
          style={{ fontFamily: 'Space Grotesk' }}
        >
          {block.text}
        </h2>
      );
    case 'p':
      return (
        <p className="text-[17px] leading-8 text-zinc-300 mb-6">
          <RichText text={block.text} />
        </p>
      );
    case 'list':
      return block.ordered ? (
        <ol className="list-decimal pl-6 space-y-3 mb-6 marker:text-orange-500 marker:font-bold">
          {block.items.map((it, i) => (
            <li key={i} className="text-[17px] leading-7 text-zinc-300 pl-1"><RichText text={it} /></li>
          ))}
        </ol>
      ) : (
        <ul className="space-y-3 mb-6">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-3 text-[17px] leading-7 text-zinc-300">
              <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
              <span><RichText text={it} /></span>
            </li>
          ))}
        </ul>
      );
    case 'callout': {
      const c = CALLOUTS[block.variant] || CALLOUTS.info;
      const Icon = c.icon;
      return (
        <div className={`my-8 rounded-2xl border ${c.wrap} p-5 md:p-6 flex gap-4`}>
          <Icon className={`w-6 h-6 shrink-0 ${c.ic}`} />
          <div>
            {block.title && (
              <p className={`font-bold uppercase tracking-wide text-xs mb-1.5 ${c.title}`}>{block.title}</p>
            )}
            <p className="text-[16px] leading-7 text-zinc-200"><RichText text={block.text} /></p>
          </div>
        </div>
      );
    }
    case 'table':
      return (
        <div className="my-8 overflow-x-auto rounded-2xl border border-zinc-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white/[0.04]">
                {block.headers.map((h, i) => (
                  <th key={i} className="text-left font-bold text-white px-4 py-3 border-b border-zinc-800 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className="odd:bg-white/[0.015]">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-4 py-3 border-b border-zinc-800/60 align-top ${ci === 0 ? 'font-semibold text-white' : 'text-zinc-300'}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'checklist':
      return (
        <div className="my-8 rounded-2xl border border-zinc-800 bg-[#161618] p-6">
          {block.title && <p className="font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>{block.title}</p>}
          <ul className="space-y-3">
            {block.items.map((it, i) => (
              <li key={i} className="flex gap-3 text-[16px] leading-7 text-zinc-300">
                <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-400 mt-0.5" />
                <span><RichText text={it} /></span>
              </li>
            ))}
          </ul>
        </div>
      );
    case 'quote':
      return (
        <blockquote className="my-8 pl-6 border-l-2 border-orange-500 relative">
          <QuoteIcon className="w-6 h-6 text-orange-500/40 mb-2" />
          <p className="text-xl md:text-2xl font-medium text-white leading-relaxed italic">{block.text}</p>
          {block.cite && <cite className="mt-3 block text-sm text-zinc-500 not-italic">— {block.cite}</cite>}
        </blockquote>
      );
    default:
      return null;
  }
};

export default ContentBlock;
